import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShoppingBag, Package, ShoppingCart, Plus } from 'lucide-react';
import { useCart } from '../context/CartContext';

const BACKEND_BASE = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000').replace(/\/$/, '');
const API = `${BACKEND_BASE}/api`;

const ALLOWED_CATEGORIES = ['Paddles', 'Balls', 'Footwear', 'Accessories', 'Equipment'];

// Helper function to format currency
const formatCurrency = (amount) => {
  return `₱${Number(amount).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const AddOnsPage = () => {
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const { addToCart, isLoggedIn } = useCart();

  useEffect(() => {
    const fetchAddons = async () => {
      try {
        const params = categoryFilter !== 'all' ? `?category=${categoryFilter}` : '';
        const response = await fetch(`${API}/addons${params}`);
        if (response.ok) {
          const data = await response.json();
          setAddons(data.filter(item => item.is_active !== false));
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAddons();
  }, [categoryFilter]);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="addons-page">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold mb-2 text-foreground">Add-ons</h1>
            <p className="text-muted-foreground">Purchase additional items for your game</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]" data-testid="category-filter">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {ALLOWED_CATEGORIES.map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Add-ons Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => <div key={i} className="h-64 bg-muted animate-pulse rounded-xl"></div>)}
          </div>
        ) : addons.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {addons.map((item) => (
              <Card
                key={item.item_id || item.equipment_id}
                className="card-hover overflow-hidden bg-white text-gray-900 border border-orange-100 shadow-sm hover:shadow-md"
                data-testid={`addon-${item.item_id || item.equipment_id}`}
              >
                <div className="h-40 bg-slate-100 flex items-center justify-center">
                  <img
                    src={item.image || 'https://images.pexels.com/photos/17299531/pexels-photo-17299531.jpeg'}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = 'https://images.pexels.com/photos/17299531/pexels-photo-17299531.jpeg'; }}
                  />
                </div>
                <CardContent className="p-5">
                  {item.category && (
                    <Badge variant="outline" className="mb-2 bg-orange-50 text-orange-800 border-orange-200">{item.category}</Badge>
                  )}
                  <h3 className="font-heading font-semibold text-lg mb-1 text-gray-900">{item.name}</h3>
                  {item.description && (
                    <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                  )}
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-bold text-[#d26a00]">{formatCurrency(item.price || item.price_per_hour || 0)}</p>
                    </div>
                    <div className="text-right">
                      <Badge
                        variant={item.available_quantity > 5 ? 'default' : 'destructive'}
                        className={item.available_quantity > 5 ? 'bg-green-100 text-green-800' : 'bg-red-50 text-red-800'}
                      >
                        {(item.available_quantity ?? 0)} available
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Add to Cart Button */}
                  <button
                    onClick={() => addToCart(item)}
                    className="w-full bg-[#004a80] text-white py-2.5 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-[#003a60] transition-colors group"
                    title={isLoggedIn ? "Add to cart" : "Log in to add to cart"}
                  >
                    <ShoppingCart className="h-5 w-5 group-hover:hidden" />
                    <Plus className="h-5 w-5 hidden group-hover:block" />
                    <span>Add to Cart</span>
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="font-heading text-lg font-semibold mb-2 text-gray-900">No add-ons available</h3>
            <p className="text-gray-700">Check back later</p>
          </div>
        )}

        {/* Info Card */}
        <Card className="mt-8 bg-gradient-to-r from-orange-50 via-white to-orange-50 border border-orange-100">
          <CardContent className="p-6 text-center text-gray-800">
            <ShoppingBag className="h-8 w-8 mx-auto text-[#d26a00] mb-3" />
            <h3 className="font-heading font-semibold mb-2">How to Purchase</h3>
            <p className="text-sm text-gray-700">
              Add items to your cart during the booking process. Items will be available for pickup at the venue.
            </p>
          </CardContent>
        </Card>
      </div>

    </Layout>
  );
};

export default AddOnsPage;